import { BarsOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputRef, Tabs } from "antd";
import { t } from "i18next";
import * as React from "react";
import { SpecificationSelect } from "src/business/specification";
import { SysDataFormItems } from "src/business/sysData/SysDataFormItems";
import {
  makeValueCatalogData,
  makeValueCatalogSave,
  ValueCatalogSave,
  ZValueCatalog,
  ZValueCatalogData,
  zValueCatalogData,
} from "src/business/valueCatalog/ZValueCatalog";
import { delay } from "src/common/delay";
import { mdmDeepEqual } from "src/common/mdmDeepEqual";
import { onError } from "src/common/onError";
import { trimRequired } from "src/common/validationRules";
import { BlankLink } from "src/components/BlankLink/BlankLink";
import { ChangeFlag } from "src/components/ChangeFlag";
import { IdBox } from "src/components/IdBox";
import { PageBlocker } from "src/components/PageBlocker";
import { ObjectCardHeader } from "src/pages/management/objects/common/ObjectCardHeader";
import * as layoutStyles from "src/pages/management/objects/common/ObjectCardLayout.module.less";
import { ObjectCardTabPane } from "src/pages/management/objects/common/ObjectCardTabPane";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { ZSidLabel } from "src/types";

import { ValueCatalogActions } from "./ValueCatalogActions";

type PropsValueCatalogForm = {
  title: string;
  okText: string;
  srcData?: ZValueCatalog;
  onSubmit: (data: ValueCatalogSave) => Promise<ZValueCatalog>;
  onSuccess: (newData: ZValueCatalog) => void;
  onDelete?: () => Promise<void>;
};

type TabKey = "main" | "system";
const tabsItems: { key: TabKey; label: string }[] = [
  { key: "main", label: "Общее" },
  { key: "system", label: "Системные данные" },
];

const name = (key: keyof ZValueCatalogData) => key;

export const ValueCatalogForm: React.FC<PropsValueCatalogForm> = (props) => {
  const { title, okText, srcData, onSubmit, onSuccess, onDelete } = props;
  const isNew = !srcData;
  const [saving, setSaving] = React.useState(false);
  const [changed, setChanged] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState<TabKey>("main");
  const [initialData, setInitialData] = React.useState<
    ZValueCatalogData | undefined
  >();
  const [form] = Form.useForm();
  const onChange = (values: unknown) => {
    setChanged(!mdmDeepEqual(values, initialData));
  };
  const firstRef = React.useRef<InputRef>(null);
  React.useEffect(() => {
    form.resetFields();
    const fields: ZValueCatalogData = srcData
      ? makeValueCatalogData(srcData)
      : {
          name: "",
          description: "",
          schema: undefined as unknown as ZSidLabel, // Это должен заполнить пользователь. Иначе всё равно не пройдёт валидацию и парсинг в save
        };
    setActiveTab("main");
    form.setFieldsValue(fields);
    setInitialData(fields);
    setChanged(false);
    if (isNew) {
      setTimeout(() => firstRef.current?.focus(), 100);
    }
  }, [srcData]);
  const save = async (values: unknown) => {
    try {
      setSaving(true);
      const data = zValueCatalogData.parse(values);
      const result = await onSubmit(makeValueCatalogSave(data));
      setChanged(false);
      await delay(1);
      onSuccess(result);
    } finally {
      setSaving(false);
    }
  };
  const requiredRule = { required: true, message: t("Field required") };
  return (
    <Form
      layout="vertical"
      form={form}
      onFinish={(values) => save(values).catch(onError)}
      onFinishFailed={() => setActiveTab("main")}
      onValuesChange={(_, values) => onChange(values)}
      className={layoutStyles.formRoot}
    >
      <PageBlocker changed={() => changed} />
      <ObjectCardHeader
        title={title}
        submitText={okText}
        submitLoading={saving}
        submitHtmlType="submit"
      >
        <ChangeFlag changed={changed} />
        {srcData?.id && (
          <BlankLink href={makeUrl(PageUrl.valueCatalog, { id: srcData.id })}>
            <Button type="link" icon={<BarsOutlined />}>
              К списку значений
            </Button>
          </BlankLink>
        )}
        <ValueCatalogActions onDelete={onDelete} />
      </ObjectCardHeader>

      <div className={layoutStyles.tabsContainer}>
        {!isNew && (
          <Tabs
            items={tabsItems}
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as TabKey)}
          />
        )}
        <div className={layoutStyles.tabContent}>
          <ObjectCardTabPane
            className={layoutStyles.commonBlock}
            hidden={activeTab !== "main"}
            hiddenClassName={layoutStyles.hidden}
          >
            <Form.Item
              name={name("name")}
              label="Системное название"
              rules={[trimRequired()]}
            >
              <Input ref={firstRef} allowClear disabled={!isNew} />
            </Form.Item>
            <IdBox entity={srcData} />
            <Form.Item name={name("description")} label="Описание">
              <Input.TextArea allowClear />
            </Form.Item>
            <Form.Item
              name={name("schema")}
              label="Спецификация"
              rules={[requiredRule]}
            >
              <SpecificationSelect specType="values" disabled={!isNew} />
            </Form.Item>
          </ObjectCardTabPane>
          <ObjectCardTabPane
            className={layoutStyles.commonBlock}
            hidden={activeTab !== "system"}
            hiddenClassName={layoutStyles.hidden}
          >
            <SysDataFormItems data={srcData} />
          </ObjectCardTabPane>
        </div>
      </div>
    </Form>
  );
};
